import { RefreshCw, Server, Circle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { formatDistanceToNow } from 'date-fns'

// Mirrors the worker shape returned by GET /api/v2/admin/solve/workers
// (backend routes/admin-solve.ts). `online` is computed server-side from the
// last heartbeat vs. workerOfflineAfterSec.
interface SolveWorker {
  workerId: string
  gpu: string | null
  hostname: string | null
  version: string | null
  status: 'idle' | 'busy'
  currentJobId: string | null
  online: boolean
  lastHeartbeatAt: string | null
  createdAt: string
}

export default function Workers() {
  const { token, adminEmail } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-solve-workers'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/api/v2/admin/solve/workers`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Admin-Email': adminEmail! },
      })
      if (!res.ok) throw new Error('Failed to load workers')
      const body = await res.json()
      return body.data as SolveWorker[]
    },
    refetchInterval: 5000, // keep the fleet view fresh
  })

  const workers = data || []
  const onlineCount = workers.filter((w) => w.online).length

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Solve Workers</h1>
          <p className="text-sm text-gray-500 mt-1">
            Registered GPU workers that pull-poll and process cloud solve jobs.
            {workers.length > 0 && (
              <>
                {' '}
                {onlineCount} of {workers.length} online.
              </>
            )}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Worker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GPU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Heartbeat
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    Loading workers...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-red-600">
                    Error loading workers. Please try again.
                  </td>
                </tr>
              ) : workers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No workers registered yet. Start one with{' '}
                    <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">python worker.py poll</code>.
                  </td>
                </tr>
              ) : (
                workers.map((w) => (
                  <tr key={w.workerId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Server className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {w.workerId}
                          </div>
                          <div className="text-xs text-gray-500">
                            {w.hostname || '—'}
                            {w.version ? ` · ${w.version}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{w.gpu || '—'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Circle
                          className={`w-2.5 h-2.5 ${
                            w.online ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-gray-300'
                          }`}
                        />
                        <span className="text-sm text-gray-700">
                          {w.online ? 'Online' : 'Offline'}
                        </span>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                            w.status === 'busy'
                              ? 'bg-indigo-100 text-indigo-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {w.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {w.currentJobId ? (
                        <span className="font-mono text-xs text-gray-700">{w.currentJobId}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {w.lastHeartbeatAt
                        ? `${formatDistanceToNow(new Date(w.lastHeartbeatAt))} ago`
                        : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
